import { Component, type OnInit, signal, computed, inject } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import _ from 'lodash';
import { AuthService } from '@card-software/nexus-utilities';
import { BreadcrumbService } from '@card-software/library';
import { Step, StepBase, StepFormGroup } from '../../../models/engineering/step';
import { ItemMaster } from '../../../models/item-master';
import { InputParameter } from '../../../models/engineering/input-parameter';
import { ItemConsumableDetails, ItemDetailsFormGroup } from '../../../models/engineering/item-consumable-details';
import { ScalingMethod } from '../../../models/engineering/enum/ScalingMethod.enum';
import { IngredientUnit } from '../../../models/engineering/enum/IngredientUnit.enum';
import { ProductDevelopmentAdminGroup, ProductDevelopmentViewerGroup } from '../../../models/authorization/enum/user-group.enum';
import { StepProxyService } from '../../../proxies/item/step.proxy.service';
import { ItemMasterProxyService } from '../../../proxies/item/item-master.proxy.service';
import { LocaleHelperService } from '../../../services/localehelperservice.service';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';

@Component({
  selector: 'app-step-details',
  templateUrl: './step-details.component.html',
  styleUrls: ['./step-details.component.scss'],
})
export class StepDetailsComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly stepProxyService = inject(StepProxyService);
  private readonly itemProxyService = inject(ItemMasterProxyService);
  private readonly translateService = inject(TranslateService);
  private readonly localeHelper = inject(LocaleHelperService);
  private readonly breadcrumbService = inject(BreadcrumbService);

  readonly step = signal<Step | null>(null);
  readonly isLoading = signal<number>(0);
  readonly inventoryTypeReference = signal<string>('6866c33a245e3504d98d3a44');

  readonly isEditing = computed(() => !_.isNil(this.step()));
  readonly canEditPermission = computed(() => {
    if (AuthService.hasRole(ProductDevelopmentAdminGroup.ProcessStepsAdmin)) {
      return true;
    }
    if (AuthService.hasRole(ProductDevelopmentViewerGroup.ProcessStepsViewer)) {
      return false;
    }
    return false;
  });

  readonly name = new FormControl<string | null>(null, {
    validators: [Validators.required],
  });
  readonly trackConsumption = new FormControl<boolean>(false, {
    nonNullable: true,
    validators: [Validators.required],
  });

  readonly itemDetailsFormGroups = new FormArray<FormGroup<ItemDetailsFormGroup>>([]);
  readonly additionalParameters = new FormControl<InputParameter[]>([], {
    nonNullable: true,
  });

  readonly formGroup = new FormGroup<StepFormGroup>({
    name: this.name,
    trackConsumption: this.trackConsumption,
    itemDetailsFormGroups: this.itemDetailsFormGroups,
    additionalParameters: this.additionalParameters,
  });

  canEdit(): boolean {
    return this.canEditPermission();
  }

  ngOnInit(): void {
    this.incrementLoading();
    this.loadStepFromRoute();
    this.setupFormPermissions();
  }

  private incrementLoading(): void {
    this.isLoading.update((current) => current + 1);
  }

  private decrementLoading(): void {
    this.isLoading.update((current) => Math.max(0, current - 1));
  }

  private loadStepFromRoute(): void {
    this.route.queryParams.subscribe((params) => {
      const stepId = params['id'];

      if (!stepId) {
        this.router.navigate(['/steps']);
        return;
      }

      if (stepId === 'new') {
        this.breadcrumbService.setCurrent('Common_New');
        return this.decrementLoading();
      }

      this.incrementLoading();
      this.stepProxyService
        .getStepById(stepId)
        .pipe(finalize(() => this.decrementLoading()))
        .subscribe({
          next: (response) => {
            if (response === null) {
              this.router.navigate(['/steps']);
            } else {
              this.step.set(response.data);
              this.breadcrumbService.setCurrent(response.data?.stepId?.toString());
              this.populateFormWithStepData();
            }
          },
        });
    });
  }

  private setupFormPermissions(): void {
    if (!this.canEdit()) {
      this.formGroup.disable();
    }
  }

  private populateFormWithStepData(): void {
    const currentStep = this.step();

    if (_.isNil(currentStep)) {
      console.error('Step is null or undefined');
      return this.decrementLoading();
    }

    this.name.setValue(currentStep.name.en);
    this.formGroup.patchValue({
      trackConsumption: currentStep.trackConsumption || false,
    });

    if (currentStep.additionalParameters && currentStep.additionalParameters.length > 0) {
      this.additionalParameters.setValue(currentStep.additionalParameters);
    }

    if (!currentStep.itemDetails || currentStep.itemDetails.length === 0) {
      return this.decrementLoading();
    }

    const distinctItemCodes = this.extractDistinctItemCodes(currentStep.itemDetails);
    this.loadItemDetailsFromApi(distinctItemCodes, currentStep);
  }

  private extractDistinctItemCodes(itemDetails: ItemConsumableDetails[]): string[] {
    return itemDetails.map((itemDetail) => itemDetail.itemCode).filter((value, index, self) => self.indexOf(value) === index);
  }

  private loadItemDetailsFromApi(itemCodes: string[], step: Step): void {
    const command: { itemCodes: string[] } = { itemCodes };

    this.itemProxyService
      .getItemCodesBatch(command)
      .pipe(finalize(() => this.decrementLoading()))
      .subscribe({
        next: (result) => {
          if (!result?.data) {
            GlobalMessageUtil.error('Failed to load item details', 'Step Details');
            return;
          }

          const itemsWithLocalizedNames = this.addLocalizedNamesToItems(result.data);
          this.createItemDetailsFormGroups(step.itemDetails, itemsWithLocalizedNames);
        },
        error: (error) => {
          console.error('Error fetching item details', error);
        },
      });
  }

  private addLocalizedNamesToItems(items: ItemMaster[]): ItemMaster[] {
    return items.map((item) => ({
      ...item,
      itemDisplayNameLocalized: this.localeHelper.getLocalizedValue(item.itemDisplayName),
    }));
  }

  private createItemDetailsFormGroups(itemDetails: ItemConsumableDetails[], items: ItemMaster[]): void {
    this.itemDetailsFormGroups.clear();

    const formGroups = itemDetails
      .map((itemDetail) => this.createSingleItemDetailFormGroup(itemDetail, items))
      .filter((formGroup): formGroup is FormGroup<ItemDetailsFormGroup> => !_.isNil(formGroup));

    formGroups.forEach((formGroup) => {
      if (!this.canEdit()) {
        formGroup.disable();
      }
      this.itemDetailsFormGroups.push(formGroup);
    });
  }

  private createSingleItemDetailFormGroup(
    itemDetail: ItemConsumableDetails,
    items: ItemMaster[],
  ): FormGroup<ItemDetailsFormGroup> | undefined {
    const item = items.find((i) => i.itemCode === itemDetail.itemCode);

    if (_.isNil(item)) {
      console.error(`Item with code ${itemDetail.itemCode} not found`);
      return undefined;
    }

    return new FormGroup<ItemDetailsFormGroup>({
      item: new FormControl<ItemMaster>(item, {
        nonNullable: true,
        validators: [Validators.required],
      }),
      isPreferred: new FormControl<boolean>(itemDetail.isPreferred, {
        nonNullable: true,
        validators: [Validators.required],
      }),
      ingredientUnit: new FormControl<IngredientUnit | null>(itemDetail.ingredientUnit ?? null),
      scalingMethod: new FormControl<ScalingMethod | null>(itemDetail.scalingMethod ?? null),
      quantity: new FormControl<number | null>(itemDetail.quantity ?? 0, {
        validators: [Validators.required],
      }),
    });
  }

  private extractAdditionalParametersFromForm(): InputParameter[] {
    return this.additionalParameters.value;
  }

  private extractItemDetailsFromForm(): ItemConsumableDetails[] {
    return this.itemDetailsFormGroups.controls.map((formGroup) => ({
      itemCode: formGroup.value.item!.itemCode!,
      isPreferred: formGroup.value.isPreferred ?? false,
      ingredientUnit: formGroup.value.ingredientUnit!,
      scalingMethod: formGroup.value.scalingMethod!,
      quantity: formGroup.value.quantity ?? 0,
    }));
  }

  private convertStepBaseToStep(stepBase: StepBase): Step {
    const currentStep = this.step();

    if (_.isNil(currentStep)) {
      throw new Error('Cannot update step: current step is null');
    }

    return {
      ...stepBase,
      id: currentStep.id,
      stepId: currentStep.stepId,
    };
  }

  onSubmit(): void {
    if (!this.isFormValidForSubmission()) {
      return;
    }

    const stepBase = this.buildStepBaseFromForm();
    const operation = this.isEditing()
      ? this.stepProxyService.updateStep(this.convertStepBaseToStep(stepBase))
      : this.stepProxyService.insertStep(stepBase);

    this.executeSaveOperation(operation, stepBase);
  }

  private isFormValidForSubmission(): boolean {
    if (this.formGroup.invalid) {
      GlobalMessageUtil.warn('Missing required fields', 'Step Details');
      return false;
    }

    const formValues = this.formGroup.value;

    if (_.isNil(formValues.name) || _.isNil(formValues.trackConsumption) || _.isNil(formValues.itemDetailsFormGroups)) {
      return false;
    }

    return true;
  }

  private buildStepBaseFromForm(): StepBase {
    const formValues = this.formGroup.value;

    return {
      name: { en: formValues.name!, es: '' },
      trackConsumption: formValues.trackConsumption!,
      notes: [],
      additionalParameters: this.extractAdditionalParametersFromForm(),
      itemDetails: this.extractItemDetailsFromForm(),
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private executeSaveOperation(operation: any, stepBase: StepBase): void {
    this.incrementLoading();

    operation.pipe(finalize(() => this.decrementLoading())).subscribe({
      next: (response: { data?: Step }) => {
        const responseStep = response.data ?? this.convertStepBaseToStep(stepBase);
        this.showSuccessMessageForStep(responseStep);
        this.navigateToStepDetailsPage(String(responseStep.stepId));
      },
    });
  }

  private showSuccessMessageForStep(step: Step): void {
    const message = this.isEditing()
      ? this.translateService.instant('Step_Update_Successfully').replace('{{name}}', step.stepId)
      : this.translateService.instant('Step_Created_Successfully', {
          name: step.stepId,
        });

    GlobalMessageUtil.success('Success', message);
  }

  private navigateToStepDetailsPage(stepId: string): void {
    this.router.navigate(['/step-details'], {
      queryParams: { id: stepId },
    });
  }
}
